import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, CalendarClock } from "lucide-react";

/** Iter 168 · Per-client MBR auto-dispatch configuration dialog. */

const SECTIONS = [
  { key: "executive_summary", label: "Executive summary (financial KPIs)" },
  { key: "engagement", label: "Engagement roll-up" },
  { key: "activity", label: "Recent activity list" },
];

export default function MbrScheduleDialog({ wsId, clientId, clientName, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/clients/${clientId}/mbr-schedule`)
      .then((r) => setCfg({ ...r.data, custom_emails_text: (r.data.custom_emails || []).join(", ") }))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load MBR schedule")));
    api.get(`/workspaces/${wsId}/clients/${clientId}/mbr-history`)
      .then((r) => setHistory(r.data.rows || []))
      .catch(() => {});
  }, [wsId, clientId]);

  const payload = () => ({
    enabled: !!cfg.enabled,
    frequency: cfg.frequency || "monthly",
    send_day: Number(cfg.send_day) || 1,
    recipients_mode: cfg.recipients_mode || "primary_contact",
    custom_emails: (cfg.custom_emails_text || "").split(/[,;\n]/).map((s) => s.trim()).filter(Boolean),
    months_window: Number(cfg.months_window) || 1,
    include_sections: cfg.include_sections || {},
    subject_template: cfg.subject_template || "",
    note: cfg.note || "",
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/workspaces/${wsId}/clients/${clientId}/mbr-schedule`, payload());
      setCfg({ ...data, custom_emails_text: (data.custom_emails || []).join(", ") });
      toast.success(data.enabled
        ? `MBR auto-send ON · next run ${data.next_run || "—"}`
        : "MBR schedule saved (auto-send off)");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      await api.put(`/workspaces/${wsId}/clients/${clientId}/mbr-schedule`, payload());
      const { data } = await api.post(
        `/workspaces/${wsId}/clients/${clientId}/mbr-schedule/send-now`);
      toast.success(data.overall === "sent"
        ? `MBR emailed to ${data.recipients.join(", ")}`
        : `MBR queued (${data.overall}) → ${data.recipients.join(", ")}`);
      const h = await api.get(`/workspaces/${wsId}/clients/${clientId}/mbr-history`);
      setHistory(h.data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSending(false); }
  };

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="mbr-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" /> Auto-send Business Review
          </DialogTitle>
          <DialogDescription>
            Schedule the MBR PDF to be emailed to {clientName || "this client"} automatically.
          </DialogDescription>
        </DialogHeader>

        {!cfg ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between border border-border p-3">
              <div>
                <div className="font-medium">Auto-send enabled</div>
                {cfg.enabled && cfg.next_run && (
                  <div className="text-xs text-muted-foreground">Next run: {cfg.next_run}</div>
                )}
              </div>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", v)}
                      data-testid="mbr-enabled-switch" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Frequency</label>
                <Select value={cfg.frequency || "monthly"} onValueChange={(v) => set("frequency", v)}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="mbr-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly (last Friday of quarter)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cfg.frequency !== "quarterly" && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-widest">Day of month</label>
                  <Input type="number" min={1} max={28} value={cfg.send_day || 1}
                         onChange={(e) => set("send_day", e.target.value)}
                         className="h-8 text-xs mt-1" data-testid="mbr-send-day-input" />
                </div>
              )}
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Data window</label>
                <Select value={String(cfg.months_window || 1)} onValueChange={(v) => set("months_window", v)}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="mbr-months-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 6, 12].map((m) => (
                      <SelectItem key={m} value={String(m)}>{m} month{m > 1 ? "s" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Recipients</label>
                <Select value={cfg.recipients_mode || "primary_contact"} onValueChange={(v) => set("recipients_mode", v)}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="mbr-recipients-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary_contact">Primary contact</SelectItem>
                    <SelectItem value="all_contacts">All contacts</SelectItem>
                    <SelectItem value="custom">Custom list</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {cfg.recipients_mode === "custom" && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Custom emails (comma-separated)</label>
                <Input value={cfg.custom_emails_text || ""}
                       onChange={(e) => set("custom_emails_text", e.target.value)}
                       placeholder="ceo@client.com, cfo@client.com"
                       className="h-8 text-xs mt-1" data-testid="mbr-custom-emails-input" />
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest">Include sections</label>
              <div className="mt-1 space-y-1.5">
                {SECTIONS.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox
                      checked={(cfg.include_sections || {})[s.key] !== false}
                      onCheckedChange={(v) => set("include_sections",
                        { ...(cfg.include_sections || {}), [s.key]: !!v })}
                      data-testid={`mbr-section-${s.key}`}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest">
                Email subject (optional — supports {"{{client_name}} {{workspace}} {{period}}"})
              </label>
              <Input value={cfg.subject_template || ""}
                     onChange={(e) => set("subject_template", e.target.value)}
                     placeholder="Your Monthly Business Review — {{period}}"
                     className="h-8 text-xs mt-1" data-testid="mbr-subject-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest">Personal note (top of email)</label>
              <textarea value={cfg.note || ""}
                        onChange={(e) => set("note", e.target.value)}
                        className="w-full h-16 text-xs p-2 border border-border rounded mt-1"
                        placeholder="Great month working together — here's the summary."
                        data-testid="mbr-note-input" />
            </div>

            {history.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest">Recent dispatches</label>
                <div className="mt-1 border border-border divide-y" data-testid="mbr-history-list">
                  {history.slice(0, 5).map((h) => (
                    <div key={h.id} className="p-2 text-xs flex items-center gap-2">
                      <span className="truncate flex-1">{h.subject}</span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        {(h.sent_at || "").slice(0, 10)}
                      </span>
                      <span className={`font-mono text-[10px] uppercase ${
                        h.delivery?.overall === "sent" ? "text-emerald-600"
                          : h.delivery?.overall === "failed" ? "text-red-600" : "text-amber-600"}`}>
                        {h.delivery?.overall || "sent"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={sendNow}
                  disabled={!cfg || sending} data-testid="mbr-send-now-btn">
            <Send className="w-3 h-3 mr-1" /> {sending ? "Sending…" : "Send now"}
          </Button>
          <Button size="sm" onClick={save} disabled={!cfg || saving}
                  data-testid="mbr-save-btn">
            {saving ? "Saving…" : "Save schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
